import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { authApi } from '../api';
import { useAuth } from '../auth-context';
import { useTestInbox } from '../mail-delivery';
import type { IVerificationRequest } from '../types';
import { TestInboxField, TestInboxNote } from './test-inbox-field';

const UNCONFIRMED =
  'The request could not be confirmed. Send it again — a new link replaces the old one.';

/**
 * "Send a new link" — `POST /auth/email/verification-requests`, the owner asking for another
 * address confirmation (survey-api's self-service plan, R14). Drawn by the prompt under the
 * header and by `/email/verify` when the link in hand could not be used, so the two cannot come
 * to ask differently.
 *
 * **Where the deployment redirects, the press opens the test inbox first.** The request's only
 * effect is the mail, so survey-api judges `deliverTo` by the whole table, `missing` included,
 * and the caller's own address is what it judges it against. A press that sent straight away
 * would be refused for an empty inbox on nearly every test account; one that always opened a
 * form would ask production for a field it does not have. So the mode decides, and the button
 * waits for the mode's read to settle before it can be pressed (`useTestInbox`'s rule).
 *
 * **The 202 says nothing about whether a mail went.** An address confirmed since the prompt was
 * drawn — in another tab, from the link a minute ago — is sent nothing, and survey-api says the
 * caller reads which from `/auth/me`. So the answer is followed by a re-read, and a prompt about
 * an address that is confirmed now goes away rather than claiming a mail. What it cannot see is
 * survey-api's limit, which sends nothing past five a window and leaves the last link good: the
 * copy after a send says so in as many words, and points at the spam folder, and promises
 * nothing more.
 *
 * **A refusal of the inbox opens the form, wherever the press came from.** With the mode unread
 * — a read that failed or timed out — the press sends at once, and on a test environment the
 * answer is `missing`: the first news of the mode. The refusal is `useTestInbox`'s to say, beside
 * the field its re-read draws, so the form has to be open for it to be seen at all.
 */
export function VerificationRequest({ email }: { email: string }) {
  const { refreshUser } = useAuth();
  const inbox = useTestInbox();
  const fieldId = React.useId();
  // The test inbox's form, open only where the deployment redirects.
  const [asking, setAsking] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState<Nullable<IVerificationRequest>>(null);
  const [failure, setFailure] = React.useState<Nullable<string>>(null);

  const send = async () => {
    setBusy(true);
    setSent(null);
    setFailure(null);
    inbox.begin();
    const request: IVerificationRequest = inbox.body;
    try {
      await authApi.requestEmailVerification(request);
      setSent(request);
      setAsking(false);
      await refreshUser();
    } catch (error) {
      // Said on the field, which is opened — or kept open — for the next try; a `not_accepted`
      // takes the field away while leaving the form, which is how "send it again" goes without.
      if (inbox.refused(error, email)) {
        setAsking(true);
        return;
      }
      console.warn('[Auth] The confirmation request was not confirmed:', error);
      setFailure(UNCONFIRMED);
    } finally {
      setBusy(false);
    }
  };

  const press = () => {
    if (inbox.delivery?.redirect) {
      setSent(null);
      setFailure(null);
      setAsking(true);
    } else {
      void send();
    }
  };

  return (
    <div className="grid gap-2">
      {asking ? (
        <form
          noValidate
          className="grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <TestInboxField inbox={inbox} id={fieldId} intended={email} disabled={busy} />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={busy || !inbox.settled}>
              {busy ? 'Sending…' : 'Send the link'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setAsking(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy || !inbox.settled}
            onClick={press}
          >
            {busy ? 'Sending…' : sent ? 'Send another link' : 'Send a new link'}
          </Button>
        </div>
      )}

      {sent && (
        <div role="status" className="grid gap-2 text-sm">
          <p>
            A new link is on its way to <strong className="break-words">{email}</strong>, unless
            several were asked for in the last hour — then the last one still works. Nothing after
            a few minutes? Check your spam folder.
          </p>
          {sent.deliverTo && (
            <TestInboxNote>
              this environment sends it to{' '}
              <strong className="break-words">{sent.deliverTo}</strong> instead.
            </TestInboxNote>
          )}
        </div>
      )}

      {failure && (
        <p role="alert" className="text-sm font-medium text-destructive">
          {failure}
        </p>
      )}
    </div>
  );
}
